import React, { useMemo, useRef, useState } from "react";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import { Download, Image as ImageIcon, FileCode, Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL } from "../components/BrandLogo";

/**
 * Brand QR code page — anyone can land here to grab a high-resolution Janté
 * QR code (PNG @ 1024 + SVG vector) for business cards, signage, host kits,
 * and event collateral. The Janté plane-logo is embedded in the center using
 * qrcode.react's `imageSettings`, with a high error-correction level so the
 * code still scans cleanly even with the logo overlay.
 *
 * The QR encodes `https://JanteTravelConcierges.com` (which redirects to
 * Sasha) — keeping it brand-canonical rather than locked to a sub-route.
 */
export const QR_DEFAULT_URL = "https://JanteTravelConcierges.com";

export default function QrPage() {
  const canvasRef = useRef(null);
  const svgWrapRef = useRef(null);
  const [url] = useState(QR_DEFAULT_URL);

  const imageSettings = useMemo(
    () => ({
      src: LOGO_URL,
      x: undefined,
      y: undefined,
      height: 96,
      width: 96,
      excavate: true,
      crossOrigin: "anonymous",
    }),
    [],
  );

  const downloadPng = () => {
    // Prefer the pre-built, high-res 1024×1024 PNG (logo overlay baked in).
    // Falls back to canvas export if the static asset isn't reachable.
    try {
      const link = document.createElement("a");
      link.download = "jante-travel-concierges-qr.png";
      link.href = "/brand/jante-qr.png";
      link.click();
      toast.success("PNG downloaded.");
    } catch (e) {
      try {
        const canvas = canvasRef.current?.querySelector("canvas");
        if (!canvas) {
          toast.error("Couldn't find the QR canvas — please reload.");
          return;
        }
        const link = document.createElement("a");
        link.download = "jante-travel-concierges-qr.png";
        link.href = canvas.toDataURL("image/png");
        link.click();
        toast.success("PNG downloaded.");
      } catch {
        toast.error("Couldn't export the PNG.");
      }
    }
  };

  const downloadSvg = () => {
    // Prefer the pre-built vector SVG so vector tools (Illustrator, Figma)
    // can scale it cleanly. Falls back to the in-page SVG snapshot.
    try {
      const link = document.createElement("a");
      link.download = "jante-travel-concierges-qr.svg";
      link.href = "/brand/jante-qr.svg";
      link.click();
      toast.success("SVG downloaded.");
    } catch (e) {
      try {
        const svgEl = svgWrapRef.current?.querySelector("svg");
        if (!svgEl) {
          toast.error("Couldn't find the SVG node.");
          return;
        }
        const cloned = svgEl.cloneNode(true);
        cloned.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        const xml = new XMLSerializer().serializeToString(cloned);
        const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
        const url2 = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = "jante-travel-concierges-qr.svg";
        link.href = url2;
        link.click();
        URL.revokeObjectURL(url2);
        toast.success("SVG downloaded.");
      } catch {
        toast.error("Couldn't export the SVG.");
      }
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch {
      toast.message("Copy unavailable — long-press the link to copy.");
    }
  };

  return (
    <div
      className="max-w-6xl mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-start"
      data-testid="qr-page"
    >
      {/* Left — copy / context */}
      <div>
        <div className="overline inline-flex items-center gap-2">
          <Sparkles size={12} strokeWidth={1.5} className="text-[var(--gold)]" />
          Brand QR · 1024 × 1024
        </div>
        <h1 className="font-display text-5xl text-[var(--emerald)] mt-3 leading-tight">
          A pocket-sized door to Sasha.
        </h1>
        <p className="mt-4 text-base text-[var(--ink-2)] leading-relaxed max-w-prose">
          Print it on a business card. Stamp it on a host-suite welcome book.
          Drop it into an Instagram tile. The Janté QR encodes{" "}
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-[var(--brand-red)] hover:underline"
            data-testid="qr-target-url"
          >
            JanteTravelConcierges.com
          </a>{" "}
          (which gracefully redirects every guest to Sasha) and carries the
          plane logo in the centre — so the brand reads even before the scan
          completes.
        </p>

        <div className="mt-6 inline-flex items-center gap-2 border border-[var(--gold)] px-4 py-2 text-xs uppercase tracking-[0.18em] text-[var(--ink-2)]">
          <span className="text-[var(--brand-red)]">Target →</span>
          <span className="tabular-nums">{url}</span>
          <button
            type="button"
            onClick={copyUrl}
            className="ml-2 inline-flex items-center gap-1 text-[var(--emerald)] hover:text-[var(--brand-red)]"
            aria-label="Copy URL"
            data-testid="qr-copy-url"
          >
            <Copy size={12} strokeWidth={1.5} /> Copy
          </button>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={downloadPng}
            className="btn-primary"
            data-testid="qr-download-png"
          >
            <ImageIcon size={14} strokeWidth={1.5} /> Download PNG
          </button>
          <button
            type="button"
            onClick={downloadSvg}
            className="btn-gold"
            data-testid="qr-download-svg"
          >
            <FileCode size={14} strokeWidth={1.5} /> Download SVG
          </button>
        </div>

        <p className="mt-6 text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] max-w-prose">
          Scans render at print sizes from 1 inch upward. Keep at least a ½-inch
          quiet zone around the code on physical collateral so cameras lock on
          fast.
        </p>
      </div>

      {/* Right — the QR itself */}
      <div className="flex flex-col items-center">
        <div
          ref={canvasRef}
          className="bg-[var(--ivory)] p-8 border-t-4 border-[var(--gold)] shadow-2xl"
          data-testid="qr-canvas-wrap"
        >
          <QRCodeCanvas
            value={url}
            size={384}
            bgColor="#FDFBF7"
            fgColor="#0A3622"
            level="H"
            includeMargin={false}
            imageSettings={imageSettings}
          />
        </div>
        {/* Hidden SVG version kept in DOM purely for the "Download SVG" path */}
        <div ref={svgWrapRef} className="sr-only" aria-hidden="true">
          <QRCodeSVG
            value={url}
            size={1024}
            bgColor="#FDFBF7"
            fgColor="#0A3622"
            level="H"
            includeMargin={false}
            imageSettings={imageSettings}
          />
        </div>
        <div className="mt-5 text-center">
          <div className="font-display text-2xl text-[var(--emerald)]">
            Janté Travel Concierges
          </div>
          <div className="overline mt-1 text-[var(--brand-red)]">
            Vacation With The Mission In Mind
          </div>
          <a
            href="tel:+14454552683"
            className="block mt-3 text-xs uppercase tracking-[0.2em] text-[var(--ink-2)] hover:text-[var(--brand-red)] transition-colors"
            data-testid="qr-concierge-phone"
          >
            <Download size={11} strokeWidth={1.5} className="inline mr-1" />
            +1 445 45Jante (52683)
          </a>
        </div>
      </div>
    </div>
  );
}
